import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import { Badge, Box, Dialog, Stack, TextareaAutosize } from '@mui/material';
import React, { useCallback, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Typography } from '@mui/joy';
import { ChatDots } from '@phosphor-icons/react';
import { useId } from '@react-aria/utils';
import axios from 'axios';
import { Formik } from 'formik';
import { useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { Button } from '~/components/ui/button';
import { Card, CardOverflow } from '~/components/ui/card';
import { useLocalizedDepartmentData } from '~/hooks/fetcher/useDepartmentData';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import {
  Sender,
  setActiveRFIConversation,
  setUnreadMessages,
} from '~/slices/rfiMessageSlice';
import {
  isRESTAPIError,
  setS3UploadFormData,
} from '../../../assets/utils/helpers';
import { generateFileUploadToS3BucketErrorResponse } from '../../../assets/utils/responseFactory';
import RFIMessages from '../../../components/common/rfiMessages';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../hooks/global/typedReduxHooks';
import requestInformationService, {
  FileUploadStartType,
} from '../../../services/requestInformationService';
import FileItem from './fileItem';
import {
  CardWrapper,
  FileUploadLabelWrapper,
  GenericBox,
  GradientWrapper,
  LabelWrapper,
  TextFieldWrapper,
  UploadButtonWrapper,
} from './styled-components';

const textAreaStyle = {
  width: '98%',
  minHeight: '400px',
  fontSize: '16px',
  background: 'transparent',
  borderRadius: '6px 6px 6px 6px',
  border: '1px solid #D6D6D6',
  padding: '12px',
};

const successStyle = {
  fontFamily: 'Schibsted Grotesk',
  color: '#181818',
  fontSize: '16px',
  fontWeight: 500,
};

type FormFieldsType = {
  details: string;
  files: FileList | null;
};
type FileReference = {
  type: string;
  label: string;
  name: string;
};

type FileItemType = {
  file_name: string;
  file_type: string;
  file_size: number;
  attachment_type: number;
};
const SecurePortalPage: React.FC = () => {
  const { slug } = useParams();

  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [files, setFiles] = useState<FileList>();
  const [fileIds, setFileIds] = useState<Array<number>>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileReferences, setFileReferences] = useState<Array<FileReference>>(
    [],
  );
  const { item, uniqueId } = useAppSelector((state) => state.securePortal);
  const [loading, setLoading] = useState<boolean>(false);
  const [initialFields] = useState({
    details: item.details,
    files: null,
  });
  const { getLocalizedDepartmentName } = useLocalizedDepartmentData();
  const { rfiConversations, unreadMessages } = useAppSelector(
    (state) => state.rfiMessage,
  );
  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const region = z.string().catch('').parse(searchParams.get('region'));

  const removeItem = (
    key: number,
    setFieldValue: (name: string, value: FileList) => void,
  ) => {
    const getFileReferences = [...fileReferences];
    getFileReferences.splice(key, 1);
    setFileReferences(getFileReferences);

    //remove from file list as well
    let newFiles = new DataTransfer();
    if (typeof files != 'undefined') {
      for (let x = 0; x < files?.length; x++) {
        if (x != key) newFiles.items.add(files[x]);
      }
      setFiles(newFiles.files);
      setFieldValue('files', newFiles.files);
    }
  };

  const handleUpload = () => {
    fileRef.current?.click();
  };

  const uploadFileActual = async (
    url: string,
    requestBody: FormData,
    name: string,
  ) => {
    try {
      const apiUrl = `${url}`;
      const response = await axios({
        method: 'post',
        url: apiUrl,
        data: requestBody,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const data = response.data;
      return data;
    } catch (err: unknown) {
      const errorMessage = "Error happened. We're currently fixing it.";
      if (isRESTAPIError(err)) {
        const resp = generateFileUploadToS3BucketErrorResponse(
          err.response.status,
          name,
        );
        throw new Error(resp);
      }
      throw new Error(errorMessage);
    }
  };

  const handleActualUpload = (
    response: { data: FileUploadStartType },
    item: File,
    items: Array<File>,
    getIndex: number,
    values: {
      details: string;
      files: FileList;
    },
    getSlug: string,
  ) => {
    setTimeout(async () => {
      const { url, id, fields } = response.data;

      let ids = fileIds;
      ids.push(id);
      setFileIds(ids);

      const requestBody = { ...fields, ...{ file: item } };
      const requestBodyForS3 = setS3UploadFormData(requestBody);
      await uploadFileActual(url, requestBodyForS3, item.name);
      await requestInformationService
        .fileUploadFinish(getSlug, id)
        .catch(() => {
          setLoading(false);
        });

      getIndex++;

      if (items[getIndex]) {
        uploadSingleFile(items[getIndex], items, getIndex, values);
        return;
      }

      await requestInformationService
        .patchRecipientData(uniqueId, {
          details: values.details,
          file_ids: fileIds,
        })
        .catch(() => {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Session expired, Please reload the page.',
            }),
            'info',
          );
          setLoading(false);
        });
      setIsSuccess(true);
      setLoading(false);
    }, 3000);
  };

  const uploadSingleFile = (
    item: File,
    items: Array<File>,
    getIndex: number,
    values: {
      details: string;
      files: FileList;
    },
  ) => {
    let { name, type, size } = item;
    let passItem: FileItemType = {
      file_name: name,
      file_type: type,
      file_size: size,
      attachment_type: 1,
    };

    let getSlug = typeof slug != 'undefined' ? slug : '';

    requestInformationService
      .fileUploadStart(getSlug, passItem)
      .then((response) => {
        handleActualUpload(response, item, items, getIndex, values, getSlug);
      })
      .catch((_) => {
        setLoading(false);
      });
  };

  const markConversationAsRead = () => {
    requestInformationService
      .rfiConversationIsReadRecipient(rfiConversations[0].id)
      .then(() => {
        dispatch(setActiveRFIConversation(rfiConversations[0].id));
        dispatch(
          setUnreadMessages({
            conversationId: rfiConversations[0].id,
            number: 0,
          }),
        );
        setIsMessageOpen(true);
      })
      .catch(() => {
        // When access token expires
        createToastNotification(
          intl.formatMessage({
            defaultMessage: 'Session expired, Please reload the page.',
          }),
          'info',
        );
      });
  };

  const validateForm = (values: FormFieldsType) => {
    const errors = {
      details: '',
      files: '',
    };

    let hasErrors: boolean = false;

    if (!values.details) {
      hasErrors = true;
      errors.details = 'Details is required.';
    }

    if (values.files == null && item.has_request_to_upload_file) {
      hasErrors = true;
      errors.files = 'File upload is required.';
    }
    return !hasErrors ? {} : errors;
  };

  const formId = useId();

  const renderRFIMessage = useCallback(
    () => (
      <RFIMessages
        rfiConversation={rfiConversations[0]}
        sender={Sender.RECIPIENT}
        recipientRegion={region}
        displayBackButton={false}
      />
    ),
    [rfiConversations],
  );

  return (
    <div className='container max-w-2xl p-4 mx-auto'>
      {/* Render RFI Messages to establish websocket connection */}
      <div className='hidden'>{renderRFIMessage()}</div>

      {/* Display RFI messages in dialog */}
      <Dialog
        open={isMessageOpen}
        onClose={() => {
          setIsMessageOpen(false);
          dispatch(setActiveRFIConversation(''));
        }}
        fullWidth
        maxWidth='md'
      >
        {renderRFIMessage()}
      </Dialog>

      {!isSuccess && item.status !== 3 && (
        <Formik
          initialValues={initialFields}
          validate={validateForm}
          onSubmit={(values, { setSubmitting }) => {
            setTimeout(() => {
              let filesArray: Array<File> = values.files ?? [];
              let index = 0;
              if (filesArray[index]) {
                setLoading(true);
                uploadSingleFile(filesArray[index], filesArray, index, {
                  details: values.details,
                  files: values.files ?? new FileList(),
                });
              } else {
                requestInformationService
                  .patchRecipientData(uniqueId, {
                    details: values.details,
                    file_ids: [],
                  })
                  .then(() => {
                    setIsSuccess(true);
                  })
                  .catch(() => {
                    createToastNotification(
                      intl.formatMessage({
                        defaultMessage:
                          'Session expired, Please reload the page.',
                      }),
                      'info',
                    );
                  });
              }
              dispatch(setActiveRFIConversation(''));
              setSubmitting(false);
            }, 400);
          }}
        >
          {({
            values,
            handleSubmit,
            errors,
            touched,
            setFieldValue,
            setFieldTouched,
          }) => (
            <div className='grid gap-4'>
              <Card>
                <CardOverflow bleed sx={{ overflow: 'hidden' }}>
                  <div className='h-6 w-full'>
                    <GradientWrapper />
                  </div>
                </CardOverflow>
                <Typography level='h4'>
                  Secure Upload: Request for Information
                </Typography>
              </Card>
              <form id={formId} onSubmit={handleSubmit}>
                <CardWrapper sx={{ mt: '16px' }}>
                  <Stack direction='row' sx={{ p: '24px' }}>
                    <Stack direction='column' sx={{ width: '50%' }}>
                      <LabelWrapper>{'Name'}</LabelWrapper>
                      <Box>
                        <TextFieldWrapper
                          value={item.recipient_name}
                          aria-readonly={true}
                          variant='outlined'
                          sx={{ width: '95%' }}
                        />
                      </Box>
                    </Stack>
                    <Stack direction='column' sx={{ width: '50%' }}>
                      <LabelWrapper>{'Email'}</LabelWrapper>
                      <Box>
                        <TextFieldWrapper
                          value={item.recipient_email}
                          variant='outlined'
                          sx={{ width: '95%' }}
                        />
                      </Box>
                    </Stack>
                  </Stack>
                </CardWrapper>
                <CardWrapper sx={{ mt: '16px' }}>
                  <Stack direction='row' sx={{ p: '24px' }}>
                    <Stack direction='column' sx={{ width: '100%' }}>
                      <LabelWrapper>{'Department'}</LabelWrapper>
                      <Box>
                        <TextFieldWrapper
                          value={getLocalizedDepartmentName(
                            item.department_name,
                          )}
                          variant='outlined'
                          sx={{ width: '98%' }}
                        />
                      </Box>
                    </Stack>
                  </Stack>
                </CardWrapper>
                <CardWrapper sx={{ mt: '16px' }}>
                  <Stack direction='row' sx={{ p: '24px' }}>
                    <Stack direction='column' sx={{ width: '100%' }}>
                      <LabelWrapper
                        sx={{ display: 'flex', alignItems: 'center' }}
                      >
                        <Box
                          className={
                            errors.details && touched.details
                              ? 'textErrorClass'
                              : ''
                          }
                          sx={{ mr: '4px' }}
                        >
                          {'Details'}
                        </Box>
                        <Box sx={{ color: '#F83B3B' }}>{'*'}</Box>
                      </LabelWrapper>
                      <Box>
                        <TextareaAutosize
                          value={values.details ?? ''}
                          maxRows={10}
                          className={
                            errors.details && touched.details
                              ? 'borderErrorClass'
                              : ''
                          }
                          onChange={(e) => {
                            const getValue = e.target.value;
                            setFieldValue('details', getValue);
                          }}
                          onBlur={() => {
                            setFieldTouched('details', true);
                          }}
                          style={textAreaStyle}
                        />
                      </Box>
                    </Stack>
                  </Stack>
                </CardWrapper>
                <CardWrapper sx={{ mt: '16px' }}>
                  <Stack direction='row' sx={{ p: '24px' }}>
                    <Stack direction='column' sx={{ width: '100%' }}>
                      <LabelWrapper
                        sx={{ display: 'flex', alignItems: 'center' }}
                      >
                        <Box sx={{ mr: '4px' }}>{'Upload Files'}</Box>
                        {item.has_request_to_upload_file && (
                          <Box sx={{ color: '#F83B3B' }}>{'*'}</Box>
                        )}
                      </LabelWrapper>
                      <Box>
                        <UploadButtonWrapper
                          onClick={handleUpload}
                          variant='outlined'
                        >
                          <FileUploadOutlinedIcon
                            sx={{ color: '#262626' }}
                            fontSize='small'
                          />
                          <Box>{'Choose Files'}</Box>
                        </UploadButtonWrapper>
                        <input
                          style={{ display: 'none' }}
                          ref={fileRef}
                          type='file'
                          multiple
                          onChange={(e) => {
                            e.preventDefault();
                            if (e.target.files) {
                              const getFiles = e.target.files;
                              setFiles(getFiles);

                              const tempFileReferences: FileReference[] = [];
                              for (const file of getFiles) {
                                tempFileReferences.push({
                                  type: file.type,
                                  label: file.name,
                                  name: file.name,
                                });
                              }
                              setFileReferences(tempFileReferences);
                              setFieldValue('files', e.target.files);
                            }
                          }}
                          name='files'
                        />
                      </Box>
                      {fileReferences.length > 0 && (
                        <Stack direction='column' sx={{ mt: '16px' }}>
                          {fileReferences.map(({ type, label }, key) => {
                            return (
                              <CardWrapper
                                key={uuidv4()}
                                sx={{ p: '16px', mb: '16px' }}
                              >
                                <Stack
                                  direction='row'
                                  justifyContent='space-between'
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'row',
                                    }}
                                  >
                                    {type.indexOf('image') != -1 && (
                                      <BrokenImageOutlinedIcon fontSize='small' />
                                    )}
                                    {type == 'application/pdf' && (
                                      <DescriptionOutlinedIcon fontSize='small' />
                                    )}
                                    {type.indexOf('video') != -1 && (
                                      <SlideshowOutlinedIcon fontSize='small' />
                                    )}
                                    {type.indexOf('video') == -1 &&
                                      type.indexOf('image') == -1 &&
                                      type != 'application.pdf' && (
                                        <DescriptionOutlinedIcon fontSize='small' />
                                      )}
                                    <FileUploadLabelWrapper sx={{ ml: '4px' }}>
                                      {label}
                                    </FileUploadLabelWrapper>
                                  </Box>
                                  <FileItem
                                    setFieldValue={setFieldValue}
                                    itemKey={key}
                                    removeItem={removeItem}
                                  />
                                </Stack>
                              </CardWrapper>
                            );
                          })}
                        </Stack>
                      )}
                    </Stack>
                  </Stack>
                </CardWrapper>
              </form>
              <Box sx={{ mt: 2 }}>{renderRFIMessage()}</Box>
              <div className='flex gap-2 items-center'>
                <Button
                  variant='outlined'
                  size='lg'
                  color='neutral'
                  shape='pill'
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  variant='outlined'
                  color='secondary'
                  shape='pill'
                  size='lg'
                  form={formId}
                  loading={loading}
                >
                  Submit
                </Button>
              </div>
            </div>
          )}
        </Formik>
      )}
      {(isSuccess || item.status === 3) && (
        <>
          <CardWrapper
            sx={{ mt: '24px', backgroundColor: 'white', w: '512px' }}
          >
            <Stack sx={{ width: '100%' }} direction='column'>
              <GradientWrapper
                sx={{ width: '100%', height: '36px' }}
              ></GradientWrapper>
              <GenericBox sx={{ p: '16px', ...successStyle }}>
                {
                  'Thank you for responding to the request for information. Your details have been securely received and we assure you that your information will be handled with the utmost confidentiality.'
                }
              </GenericBox>
            </Stack>
          </CardWrapper>
          <div className='absolute right-10 top-20 h-16 w-16'>
            <Badge
              badgeContent={unreadMessages[rfiConversations[0].id]}
              overlap='circular'
              color='error'
            >
              <button
                className='flex items-center justify-center bg-blue-600 h-16 w-16 rounded-full shadow-md shadow-gray-400 cursor-pointer'
                onClick={markConversationAsRead}
              >
                <ChatDots size={32} color='white' />
              </button>
            </Badge>
          </div>
        </>
      )}
    </div>
  );
};

export default SecurePortalPage;
